import type { QueryClient } from '@tanstack/react-query';
import { ApiError } from './api.js';

/**
 * Back to the PIN pad the moment the counter's session ends — features/AUTH.md.
 *
 * A staff session can end mid-shift: the owner removes the barista or resets
 * their PIN (`StaffMember.sessionVersion`), or unpairs the device. Every
 * request after that is a 401, and without this the counter showed the 401's
 * message as an error and only reached the sign-in screen on its next session
 * check. Re-asking `/auth/me` lets `RequireStaff` see the signed-out answer and
 * redirect, and the sign-in screen tells "device removed" from "sign in again".
 *
 * Only on `/staff` and not on its sign-in and pairing screens, where a 401 is the expected
 * answer to a wrong PIN rather than a session that ended.
 *
 * Never for a failure of the session queries themselves (`['auth', …]`). A 401
 * from `/auth/me` *is* the signed-out answer; re-asking it put the query back
 * to pending, so `RequireStaff` held its spinner instead of redirecting and
 * `/staff` looped `me` → `refresh` until the throttler answered 429.
 */
export function endStaffSessionOn401(queryClient: QueryClient) {
  return (error: unknown, queryKey?: readonly unknown[]): void => {
    if (!(error instanceof ApiError) || error.status !== 401) return;
    if (queryKey?.[0] === 'auth') return;
    const path = typeof window === 'undefined' ? '' : window.location.pathname;
    if (!path.startsWith('/staff') || /^\/staff\/(login|pair)/.test(path)) return;
    void queryClient.invalidateQueries({ queryKey: ['auth', 'me'], exact: true });
  };
}
